import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Newspaper, 
  Globe, 
  PartyPopper, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Trophy,
  ArrowRight,
  Phone,
  Mail,
  ExternalLink,
  Menu,
  X,
  MapPin,
  Calendar
} from "lucide-react";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/91dtn44o_jambassador-standard.png";
const VENUE_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/42wb5bzn_JHL%20Antun%27s%20Image.png";

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      {/* Navigation */}
      <header 
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-sm" 
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-28">
            <a href="#top" className="flex items-center" data-testid="nav-brand">
              <img 
                src={LOGO}
                alt="JamBassador Awards Logo" 
                className="h-20 md:h-24 w-auto"
              />
            </a>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="font-sans text-sm font-medium text-[#132238] hover:text-[#0b3d91] transition-colors" data-testid="nav-about">About</a>
              <a href="#process" className="font-sans text-sm font-medium text-[#132238] hover:text-[#0b3d91] transition-colors" data-testid="nav-process">Process</a>
              <a href="#venue" className="font-sans text-sm font-medium text-[#132238] hover:text-[#0b3d91] transition-colors" data-testid="nav-venue">Venue</a>
              <a href="#benefits" className="font-sans text-sm font-medium text-[#132238] hover:text-[#0b3d91] transition-colors" data-testid="nav-benefits">Benefits</a>
              <a href="#contact" className="font-sans text-sm font-medium text-[#132238] hover:text-[#0b3d91] transition-colors" data-testid="nav-contact">Contact</a>
              <Link 
                to="/apply" 
                className="px-6 py-3 bg-[#f2c94c] text-[#132238] font-sans font-semibold text-sm rounded-full hover:bg-[#e5bc3f] transition-all duration-300 shadow-lg hover:shadow-xl"
                data-testid="nav-apply-btn"
              >
                Apply Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-[#132238]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-100 py-4" data-testid="mobile-menu">
              <div className="flex flex-col gap-4 px-4">
                <a href="#about" className="font-sans text-sm font-medium text-[#132238]" onClick={() => setMobileMenuOpen(false)}>About</a>
                <a href="#process" className="font-sans text-sm font-medium text-[#132238]" onClick={() => setMobileMenuOpen(false)}>Process</a>
                <a href="#venue" className="font-sans text-sm font-medium text-[#132238]" onClick={() => setMobileMenuOpen(false)}>Venue</a>
                <a href="#benefits" className="font-sans text-sm font-medium text-[#132238]" onClick={() => setMobileMenuOpen(false)}>Benefits</a>
                <a href="#contact" className="font-sans text-sm font-medium text-[#132238]" onClick={() => setMobileMenuOpen(false)}>Contact</a>
                <Link 
                  to="/apply" 
                  className="px-6 py-3 bg-[#f2c94c] text-[#132238] font-sans font-semibold text-sm rounded-full text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Apply Now
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section 
        id="top" 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/1c119849-6b6e-4fb3-bc80-288f24adee12/images/6cd24bdaf260d3c7886abfd88e08d3b19d8bf53b9798b78b661a80be1327830e.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#132238]/95 via-[#132238]/80 to-[#0b3d91]/40" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block font-sans uppercase tracking-[0.2em] text-sm font-semibold text-[#f2c94c] mb-4" data-testid="hero-pill">
                Applications now open • Limited profiles
              </span>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[0.95] mb-6" data-testid="hero-title">
                You've Been Identified.<br />
                <span className="text-[#f2c94c]">Now Step Into Your Legacy.</span>
              </h1>
              <p className="font-sans text-lg text-white/90 leading-relaxed max-w-xl mb-8" data-testid="hero-description">
                Apply to become a <strong>JamBassador 2026</strong> — a distinguished recognition initiative celebrating Jamaicans making a global impact across business, music, arts, education, government, and community leadership.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-12">
                <Link 
                  to="/apply"
                  className="px-8 py-4 bg-[#f2c94c] text-[#132238] font-sans font-bold text-base rounded-full hover:bg-[#e5bc3f] transition-all duration-300 shadow-xl hover:shadow-2xl flex items-center gap-2"
                  data-testid="hero-apply-btn"
                >
                  Apply Now <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/nominate"
                  className="px-8 py-4 bg-transparent border-2 border-white/30 text-white font-sans font-semibold text-base rounded-full hover:bg-white/10 transition-all duration-300"
                  data-testid="hero-nominate-btn"
                >
                  Nominate Someone
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4" data-testid="hero-stats">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
                  <span className="block font-serif text-3xl sm:text-4xl font-bold text-[#f2c94c]">7</span>
                  <span className="font-sans text-sm text-white/80">Categories</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
                  <span className="block font-serif text-3xl sm:text-4xl font-bold text-[#f2c94c]">Oct 17</span>
                  <span className="font-sans text-sm text-white/80">Awards Luncheon</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
                  <span className="block font-serif text-3xl sm:text-4xl font-bold text-[#f2c94c]">2026</span>
                  <span className="font-sans text-sm text-white/80">Cohort Year</span>
                </div>
              </div>
            </div>

            {/* Hero Card */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
                <img 
                  src={VENUE_IMG}
                  alt="Heroes Awards Luncheon at Antun's"
                  className="w-full rounded-2xl shadow-xl"
                  data-testid="hero-image"
                />
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="font-sans text-sm text-white/90 text-center">
                    <strong className="text-[#2bb673]">Net proceeds benefit Sickle Cell Trust.</strong> Selected honorees receive print, digital, radio, and Awards Luncheon recognition in celebration of Jamaica's 64th year of Independence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 md:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-sans uppercase tracking-[0.2em] text-sm font-semibold text-[#2bb673]">About the initiative</span>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#132238] mt-2 mb-4">
            A curated platform for Jamaican excellence.
          </h2>
          <p className="font-sans text-lg text-[#4b5563] max-w-3xl mb-12">
            JamBassadors is more than a profile feature. It is a multimedia recognition platform built to spotlight Jamaicans across the diaspora whose work reflects excellence, leadership, service, and cultural impact.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div 
              className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(11,61,145,0.08)]"
              data-testid="about-card-print"
            >
              <div className="w-14 h-14 rounded-xl bg-[#0b3d91]/10 flex items-center justify-center mb-6">
                <Newspaper className="w-7 h-7 text-[#0b3d91]" />
              </div>
              <h3 className="font-serif text-2xl text-[#0b3d91] mb-3">National Print Recognition</h3>
              <p className="font-sans text-[#4b5563]">
                A professionally written feature in the <strong>Jamaica Gleaner North American Independence Edition</strong>.
              </p>
            </div>

            <div 
              className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(11,61,145,0.08)]"
              data-testid="about-card-digital"
            >
              <div className="w-14 h-14 rounded-xl bg-[#0b3d91]/10 flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-[#0b3d91]" />
              </div>
              <h3 className="font-serif text-2xl text-[#0b3d91] mb-3">Digital Presence</h3>
              <p className="font-sans text-[#4b5563]">
                A dedicated online profile at <strong>JamBassadorAwards.com</strong> with links to your website and social platforms.
              </p>
            </div>

            <div 
              className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(11,61,145,0.08)]"
              data-testid="about-card-gala"
            >
              <div className="w-14 h-14 rounded-xl bg-[#0b3d91]/10 flex items-center justify-center mb-6">
                <PartyPopper className="w-7 h-7 text-[#0b3d91]" />
              </div>
              <h3 className="font-serif text-2xl text-[#0b3d91] mb-3">Awards Luncheon Honors</h3>
              <p className="font-sans text-[#4b5563]">
                Formal recognition at the <strong>Heroes Awards Luncheon on Saturday, October 17th, 2026</strong> at Antun's, Queens NY.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 md:py-32 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-sans uppercase tracking-[0.2em] text-sm font-semibold text-[#2bb673]">Selection process</span>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#132238] mt-2 mb-4">
            Prestige comes through review, not pay-to-play.
          </h2>
          <p className="font-sans text-lg text-[#4b5563] max-w-3xl mb-12">
            To protect the integrity of the platform, all submissions are screened for impact, reputation, and alignment with the values of the initiative.
          </p>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-16">
            {[
              { num: "1", title: "Apply or Be Nominated", desc: "Submit a brief application outlining your work, impact, achievements, and area of contribution." },
              { num: "2", title: "Selection Review", desc: "Applications are evaluated based on professional excellence, community contribution, and alignment with program values." },
              { num: "3", title: "Official Invitation", desc: "Selected candidates are invited to participate as official JamBassadors 2026." }
            ].map((step, i) => (
              <div 
                key={i}
                className="relative bg-gradient-to-b from-white to-[#f8fbff] rounded-3xl p-8 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden"
                data-testid={`process-step-${step.num}`}
              >
                <span className="absolute right-4 top-2 font-serif text-7xl font-bold text-[#f2c94c]/20">{step.num}</span>
                <h3 className="font-serif text-2xl text-[#0b3d91] mb-3 relative z-10">{step.title}</h3>
                <p className="font-sans text-[#4b5563] relative z-10">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* If Selected Section */}
          <div id="apply">
            <div className="bg-[#132238] rounded-3xl p-8 md:p-12 border border-white/10" data-testid="if-selected-box">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="font-sans uppercase tracking-[0.2em] text-sm font-semibold text-[#2bb673]">If selected</span>
                  <h3 className="font-serif text-3xl md:text-4xl text-white mt-2 mb-6">
                    Selected honorees receive a premium recognition package.
                  </h3>
                  <p className="font-sans text-white/80 mb-6">
                    Selected candidates will be contacted directly with full details about participation.
                  </p>
                  <Link 
                    to="/apply"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-[#f2c94c] text-[#132238] font-sans font-bold text-base rounded-full hover:bg-[#e5bc3f] transition-all duration-300 shadow-xl"
                    data-testid="request-application-btn"
                  >
                    Apply Now <ArrowRight size={18} />
                  </Link>
                </div>
                
                <ul className="space-y-3">
                  {[
                    "Feature in the Jamaica Gleaner North American Independence Edition",
                    "Dedicated online profile with clickable links",
                    "Radio and digital media exposure",
                    "Awards Luncheon recognition on Saturday, October 17th, 2026",
                    "Award presentation, media coverage, and community visibility"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#f2c94c] flex-shrink-0 mt-0.5" />
                      <span className="font-sans text-white/90">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Venue Section */}
      <section id="venue" className="py-20 md:py-32 bg-[#132238]" data-testid="venue-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-sans uppercase tracking-[0.2em] text-sm font-semibold text-[#f2c94c]">Save the date</span>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mt-2 mb-6">
                Heroes Awards Luncheon
              </h2>
              
              <div className="flex flex-col gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-[#f2c94c] flex-shrink-0" />
                  <div>
                    <p className="font-sans text-sm text-white/70 uppercase tracking-wider">Date</p>
                    <p className="font-serif text-xl text-white">Saturday, October 17th, 2026</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-[#2bb673] flex-shrink-0" />
                  <div>
                    <p className="font-sans text-sm text-white/70 uppercase tracking-wider">Venue</p>
                    <p className="font-serif text-xl text-white">Antun's</p>
                    <p className="font-sans text-white/80">Queens, New York</p>
                  </div>
                </div>
              </div>

              <p className="font-sans text-white/80 max-w-xl mb-8 leading-relaxed">
                Join us at Antun's, one of Queens' most distinguished venues, for a celebration of Jamaican excellence. The Heroes Awards Luncheon brings together honorees, community leaders, media, and supporters in an elegant setting befitting the occasion.
              </p>

              <a
                href="https://www.google.com/maps/search/Antun%27s+Queens+NY"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#f2c94c] text-[#132238] font-sans font-bold text-sm rounded-full hover:bg-[#e5bc3f] transition-all duration-300 shadow-xl"
                data-testid="venue-directions-btn"
              >
                Get Directions <ArrowRight size={16} />
              </a>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-[#f2c94c]/20">
                <img
                  src={VENUE_IMG}
                  alt="Heroes Awards Luncheon at Antun's, Queens NY"
                  className="w-full h-auto"
                  data-testid="venue-image"
                />
              </div>
              <div className="absolute -top-4 -right-4 bg-[#2bb673] text-white font-sans font-bold px-6 py-3 rounded-full shadow-xl transform rotate-3">
                Oct 17, 2026
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 md:py-32" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-sans uppercase tracking-[0.2em] text-sm font-semibold text-[#2bb673]">Why apply</span>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#132238] mt-2 mb-4">
            Recognition that builds visibility, credibility, and legacy.
          </h2>
          <p className="font-sans text-lg text-[#4b5563] max-w-3xl mb-12">
            JamBassadors is designed to elevate personal brands while documenting Jamaican excellence across the diaspora.
          </p>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-8">
            {[
              { icon: TrendingUp, title: "Elevate Your Brand", desc: "Increase visibility across respected print, digital, and radio channels." },
              { icon: Users, title: "Expand Your Network", desc: "Connect with leaders, sponsors, honorees, and supporters across the diaspora." },
              { icon: Trophy, title: "Create Lasting Legacy", desc: "Document your impact through a public recognition platform with ongoing visibility." }
            ].map((benefit, i) => (
              <div 
                key={i}
                className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(11,61,145,0.08)]"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[#0b3d91]/10 flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#0b3d91]" />
                </div>
                <h3 className="font-serif text-2xl text-[#0b3d91] mb-3">{benefit.title}</h3>
                <p className="font-sans text-[#4b5563]">{benefit.desc}</p>
              </div>
            ))}
          </div>

          {/* Category Tags */}
          <div className="flex flex-wrap gap-3" data-testid="category-tags">
            {["Business", "Music", "Arts", "Education", "Government", "Community Leadership", "Sports"].map((tag, i) => (
              <span 
                key={i}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-full border border-gray-200 font-sans text-sm font-medium text-[#0b3d91] shadow-sm"
              >
                <span className="w-2 h-2 rounded-full bg-[#2bb673]" />
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Band - Sickle Cell Trust */}
      <section id="nominate" className="py-16 md:py-20" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#132238] rounded-3xl p-8 md:p-12 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <span className="font-sans uppercase tracking-[0.2em] text-sm font-semibold text-[#2bb673]">A purpose-driven initiative</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mt-2 mb-4">
                Net proceeds benefit Sickle Cell Trust.
              </h2>
              <p className="font-sans text-white/80">
                JamBassadors pairs recognition with real impact by advancing awareness and support for individuals and families affected by Sickle Cell disease in African American and Caribbean communities.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/nominate"
                className="px-8 py-4 bg-[#f2c94c] text-[#132238] font-sans font-bold text-base rounded-full hover:bg-[#e5bc3f] transition-all duration-300 shadow-xl"
                data-testid="cta-nominate-btn"
              >
                Nominate Someone
              </Link>
              <a 
                href="#contact"
                className="px-8 py-4 bg-transparent border-2 border-white/30 text-white font-sans font-semibold text-base rounded-full hover:bg-white/10 transition-all duration-300"
                data-testid="cta-question-btn"
              >
                Ask a Question
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-16 md:py-20 border-t border-gray-200" data-testid="footer-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <img 
                src={LOGO}
                alt="JamBassador Awards Logo" 
                className="h-32 md:h-40 w-auto mb-4"
              />
              <p className="font-sans text-[#4b5563] max-w-md">
                A distinguished multimedia platform celebrating Jamaicans making a difference across the diaspora — online, in print, on radio, and at the Heroes Awards Luncheon on Saturday, October 17th, 2026 at Antun's, Queens NY.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div data-testid="contact-carmelita">
                  <p className="font-sans font-semibold text-[#132238]">Carmelita Smith</p>
                  <a href="tel:9179133483" className="font-sans text-[#4b5563] hover:text-[#0b3d91] flex items-center gap-2">
                    <Phone size={16} /> (917) 913-3483
                  </a>
                </div>
                <div data-testid="contact-patrick">
                  <p className="font-sans font-semibold text-[#132238]">Patrick Buddington</p>
                  <a href="tel:9174177952" className="font-sans text-[#4b5563] hover:text-[#0b3d91] flex items-center gap-2">
                    <Phone size={16} /> (917) 417-7952
                  </a>
                </div>
              </div>
              <div className="space-y-4">
                <div data-testid="contact-email">
                  <p className="font-sans font-semibold text-[#132238]">Email</p>
                  <a href="mailto:Info@imcmedia360.com" className="font-sans text-[#4b5563] hover:text-[#0b3d91] flex items-center gap-2">
                    <Mail size={16} /> Info@imcmedia360.com
                  </a>
                </div>
                <div data-testid="contact-website">
                  <p className="font-sans font-semibold text-[#132238]">Website</p>
                  <a href="https://www.JamBassadorAwards.com" target="_blank" rel="noopener noreferrer" className="font-sans text-[#4b5563] hover:text-[#0b3d91] flex items-center gap-2">
                    <ExternalLink size={16} /> www.JamBassadorAwards.com
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="font-sans text-sm text-[#4b5563]">
              © 2025 JamBassadors Awards. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
